import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { DownloadProgress, downloadStreamedCsv } from './streaming-download';

const BASE = `${DEV_NEW_DOMAIN_URL}/ble-pings/admin`;

/** One heat-map cell: the mean count in one size channel over one time bucket. */
export type BleChannelCell = {
  /** Bucket start, unix ms. */
  t: number;
  /** Zero-based channel index. */
  channel: number;
  value: number;
  /** Pings averaged into this cell. */
  n: number;
};

/**
 * Bucketed size-channel data for the heat map.
 *
 * `rows` is SPARSE — a bucket with no pings is simply absent, as is a channel
 * that never carried a number. The chart has to treat a missing cell as "no
 * data" and not as zero, or a gap in collection reads as clean air.
 */
export type BleChannelsResponse = {
  deviceSerial: number;
  key: string;
  from: number;
  to: number;
  bucketMs: number;
  channelCount: number;
  rawCount: number;
  min: number | null;
  max: number | null;
  rows: BleChannelCell[];
};

/**
 * Size-channel array over a range, averaged into buckets server-side.
 * @param serial - Device serial.
 * @param from - Range start, unix ms.
 * @param to - Range end, unix ms.
 * @param maxBuckets - Bucket ceiling; 0 lets the server choose.
 * @returns The bucketed cells plus the colour-scale extent.
 */
export const getBleChannels = async (
  serial: number,
  from: number,
  to: number,
  maxBuckets = 0
): Promise<BleChannelsResponse> => {
  const res = await axiosInstance.get<BleChannelsResponse>(`${BASE}/channels`, {
    params: { serial, from, to, maxBuckets }
  });
  return res.data;
};

/**
 * Filename for a download, so a folder of exports stays identifiable.
 * @param serial - Device serial.
 * @param from - Range start, unix ms.
 * @param to - Range end, unix ms.
 * @returns Filename carrying the serial and both timestamps.
 */
const exportFilename = (serial: number, from: number, to: number): string => {
  /**
   * Compact UTC stamp for a filename.
   * @param ms - Unix ms.
   * @returns Stamp like "20261101-1530".
   */
  const stamp = (ms: number): string =>
    new Date(ms).toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-');
  return `ble-pings-${serial}-${stamp(from)}-to-${stamp(to)}.csv`;
};

/**
 * Downloads every raw ping in a range as CSV.
 *
 * Raw rows, not the averages the charts show: the point of an export is to get
 * underneath the downsampling. A 1 Hz device makes ~86k rows a day, so the
 * server streams it and this writes it to disk as it arrives rather than
 * buffering the whole file in the tab.
 * @param serial - Device serial.
 * @param from - Range start, unix ms.
 * @param to - Range end, unix ms.
 * @param onProgress - Called with the running byte count.
 * @returns Resolves once written, or immediately if the user cancels the save.
 */
export const downloadBlePingCsv = async (
  serial: number,
  from: number,
  to: number,
  onProgress?: DownloadProgress
): Promise<void> => {
  const params = new URLSearchParams({
    serial: String(serial),
    from: String(from),
    to: String(to)
  });
  await downloadStreamedCsv(
    `${BASE}/export?${params}`,
    exportFilename(serial, from, to),
    onProgress
  );
};
